import { useCallback, useRef, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button-variants";
import { useApp } from "@/hooks/use-app-context";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from "@/hooks/use-confirm";

/**
 * Mounted once at the root. Any component can then ask
 * `if (await confirm({ title })) { ... }` instead of window.confirm,
 * which blocks the page and can't be styled or translated.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useApp();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  const danger = options?.tone === "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={options !== null} onOpenChange={(open) => !open && settle(false)}>
        <AlertDialogContent className="max-w-md gap-5 rounded-xl border-border/60 bg-background p-6 dark:bg-background/95">
          <AlertDialogHeader className="flex-row items-start gap-4 space-y-0 text-left">
            {danger ? (
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-danger/10 text-danger">
                <Icon name="alertTriangle" className="size-5" />
              </div>
            ) : null}
            <div className="space-y-1.5">
              <AlertDialogTitle className="text-base font-semibold">{options?.title}</AlertDialogTitle>
              {options?.description ? (
                <AlertDialogDescription className="text-sm leading-6 text-muted-foreground">
                  {options.description}
                </AlertDialogDescription>
              ) : null}
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className={cn(buttonVariants({ variant: "outline", size: "sm" }), "mt-0")} onClick={() => settle(false)}>
              {options?.cancelLabel ?? t.cancel ?? "Annuler"}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: danger ? "destructive" : "default", size: "sm" }))}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? t.confirm ?? "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
